import { zodResolver } from '@hookform/resolvers/zod';
import { Button, TextField, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { trpc } from '../../../../trpc/react';
import { Drawer, DrawerFooter, DrawerHeader } from '../../../components/drawer';
import { Form, FormField, FormItem } from '../../../components/form/form';
import { useToast } from '../../../hooks/useToast';
import type { AdminCategory } from './types';
import { isDuplicateCategoryName } from './utils';

export const zCategoryForm = z.object({
  name: z.string().trim().min(1, 'Name is required'),
});

type CategoryFormValues = z.infer<typeof zCategoryForm>;

/**
 * Create/edit drawer for a metric category. The category list is passed in so
 * the duplicate check matches the server rule without a round trip; the server
 * still rejects a race with a CONFLICT, surfaced as an error toast.
 */
export function CategoryDrawer({
  open,
  onClose,
  orgId,
  category,
  categories,
  onSuccess,
}: {
  open: boolean;
  onClose: () => void;
  orgId: string;
  category?: AdminCategory;
  categories: AdminCategory[];
  onSuccess: () => void;
}) {
  const { toast } = useToast();
  const isEditMode = !!category;

  const form = useForm<CategoryFormValues>({
    mode: 'onChange',
    resolver: zodResolver(zCategoryForm),
    defaultValues: { name: category?.name ?? '' },
  });

  const {
    control,
    handleSubmit,
    watch,
    formState: { isValid },
  } = form;

  const name = watch('name');
  const isDuplicate = isDuplicateCategoryName(
    name ?? '',
    categories,
    category?.id,
  );

  const createMutation = trpc.admin.metrics.createCategory.useMutation({
    onSuccess: () => {
      toast({ title: 'Category created', severity: 'success' });
      onSuccess();
    },
    onError: () => {
      toast({ title: 'Error creating category', severity: 'error' });
    },
  });

  const updateMutation = trpc.admin.metrics.updateCategory.useMutation({
    onSuccess: () => {
      toast({ title: 'Category updated', severity: 'success' });
      onSuccess();
    },
    onError: () => {
      toast({ title: 'Error updating category', severity: 'error' });
    },
  });

  // Fire-and-forget: success/error are handled by the mutation callbacks.
  // Awaiting mutateAsync here would let a server error escape handleSubmit as
  // an unhandled rejection.
  const onSubmit = (data: CategoryFormValues) => {
    if (isDuplicate) {
      return;
    }

    if (isEditMode && category) {
      updateMutation.mutate({
        orgId,
        id: category.id,
        name: data.name,
      });
    } else {
      createMutation.mutate({ orgId, name: data.name });
    }
  };

  return (
    <Drawer open={open} onClose={onClose}>
      <DrawerHeader onClose={onClose}>
        <Typography variant="h3">
          {isEditMode ? 'Edit Category' : 'Create Category'}
        </Typography>
      </DrawerHeader>

      <Form {...form}>
        <form
          id="category-form"
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col grow px-6 gap-4 overflow-y-auto"
        >
          <FormField
            name="name"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <FormItem>
                <TextField
                  {...field}
                  label="Name"
                  autoFocus
                  error={Boolean(error) || isDuplicate}
                  helperText={
                    error?.message ??
                    (isDuplicate
                      ? 'A category with this name already exists'
                      : '')
                  }
                  fullWidth
                />
              </FormItem>
            )}
          />
        </form>
      </Form>

      <DrawerFooter className="flex flex-row gap-2 w-full p-6 justify-end">
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="submit"
          form="category-form"
          variant="contained"
          disabled={
            !isValid ||
            isDuplicate ||
            createMutation.isLoading ||
            updateMutation.isLoading
          }
        >
          {isEditMode ? 'Save' : 'Create'}
        </Button>
      </DrawerFooter>
    </Drawer>
  );
}
